import { getCurrentDenkerWs } from "@/api/canvas-ws-ref";
import { useAgentStore } from "@/stores/agent-store";
import {
  useCompanionStore,
  type ActiveExecution,
} from "@/stores/companion-store";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { useProjectStore } from "@/stores/project-store";
import { agentPresentationController } from "@/lib/agent-presentation-controller";

function presentingExecutionId(): string | undefined {
  const presentation = agentPresentationController.getSnapshot();
  if (
    !presentation ||
    presentation.lifecycle === "completed" ||
    presentation.lifecycle === "cancelled"
  )
    return undefined;
  return presentation.executionId;
}

function isActiveExecution(exec: ActiveExecution): boolean {
  return exec.status === "running" || exec.status === "waiting_approval";
}

function agentKeyForProjectAgent(projectAgentId: string | null): string | null {
  if (!projectAgentId) return null;
  const agent = useAgentStore
    .getState()
    .agents.find((candidate) => candidate.id === projectAgentId);
  const key = agent?.template_name?.toLowerCase();
  return key || null;
}

function stopExecution(executionId: string): boolean {
  const ws = getCurrentDenkerWs();
  if (!ws) return false;
  ws.stopExecution(executionId);
  return true;
}

/**
 * The one active execution a stop for runtime session `sessionId` targets:
 * the one presenting when it belongs to the session, else the only match;
 * null when none or several match.
 */
export function activeExecutionForSession(
  sessionId: string,
  spaceId?: string | null,
): string | null {
  const companion = useCompanionStore.getState();
  const matchingExecutionIds = new Set<string>();
  const addIfActiveInSpace = (executionId: string): void => {
    const execution = companion.executions.get(executionId);
    if (!execution || !isActiveExecution(execution)) return;
    if (spaceId && execution.spaceId !== spaceId) return;
    if (execution.runtimeSessionId && execution.runtimeSessionId !== sessionId)
      return;
    matchingExecutionIds.add(executionId);
  };

  for (const execution of companion.executions.values()) {
    if (execution.runtimeSessionId === sessionId) {
      addIfActiveInSpace(execution.id);
    }
  }
  for (const session of Object.values(companion.agentSessions)) {
    if (session.runtimeSessionId !== sessionId) continue;
    if (spaceId && session.spaceId !== spaceId) continue;
    addIfActiveInSpace(session.executionId);
  }

  const presentingId = presentingExecutionId();
  if (presentingId && matchingExecutionIds.has(presentingId)) {
    return presentingId;
  }
  if (matchingExecutionIds.size !== 1) return null;
  return [...matchingExecutionIds][0]!;
}

export function stopActiveExecutionForSession(
  sessionId: string,
  spaceId?: string | null,
): boolean {
  const executionId = activeExecutionForSession(sessionId, spaceId);
  return executionId !== null && stopExecution(executionId);
}

export async function stopCurrentAgentSession(): Promise<boolean> {
  const spaceId = useProjectStore.getState().currentSpace?.id;
  if (!spaceId) return false;

  const explicitTarget = useInputBarTarget.getState().target;
  const selectedAgentId =
    explicitTarget?.projectAgentId ?? useAgentStore.getState().selectedAgentId;
  const selectedAgentKey = agentKeyForProjectAgent(selectedAgentId);
  const companion = useCompanionStore.getState();

  const targetedSessionId = explicitTarget?.runtimeSessionId;
  if (targetedSessionId) {
    return stopActiveExecutionForSession(targetedSessionId, spaceId);
  }

  if (!selectedAgentKey) return false;

  const activeSession = companion.getActiveSession(selectedAgentKey, spaceId);
  if (activeSession?.executionId) {
    const presentingId = presentingExecutionId();
    const presenting = presentingId
      ? companion.executions.get(presentingId)
      : undefined;
    if (
      presenting &&
      isActiveExecution(presenting) &&
      presenting.spaceId === spaceId &&
      presenting.agentKey === selectedAgentKey &&
      activeSession.runtimeSessionId &&
      presenting.runtimeSessionId === activeSession.runtimeSessionId
    ) {
      return stopExecution(presenting.id);
    }
    const execution = companion.executions.get(activeSession.executionId);
    if (
      execution &&
      isActiveExecution(execution) &&
      execution.spaceId === spaceId &&
      execution.agentKey === selectedAgentKey &&
      (!activeSession.runtimeSessionId ||
        execution.runtimeSessionId === activeSession.runtimeSessionId)
    ) {
      return stopExecution(activeSession.executionId);
    }
  }
  return false;
}
